import { memo, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-use';

import {
  Flex,
  IconHouseOutline,
  IconLiveNow,
  IconPlus,
  MenuItem,
  MenuPopper,
  Spacer,
  Text,
  Tooltip,
} from '@zillow/constellation';

import { StyledIconButton, StyledNavigationDropdownMenu, StyledTooltipPopper } from './styles';
import AdaptiveAnchor from '../AdaptiveAnchor';
import { HeaderTitles } from '../../constants';
import HeaderNavigationClickstreamEvents from '../../models/HeaderNavigationClickstreamEvents';

import type { User } from '../../interfaces/user';

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Link?: React.ComponentType<React.PropsWithChildren<any>>;
  canAccessAddFeedListingsModal: boolean;
  escapeAllLinks?: boolean;
  handleNoFeedIconPlusClick?: (pageClickingTo: string) => void | null;
  onClickPropertyMenuItem?: (event: React.SyntheticEvent) => void | null;
  onClose?: () => void | null;
  onOpen?: () => void | null;
  propertiesPage: string;
  useAbsolutePaths?: boolean;
  userModel: User;
};

const PlusIconPropertyMenu = ({
  Link,
  canAccessAddFeedListingsModal,
  escapeAllLinks = false,
  handleNoFeedIconPlusClick = () => {},
  onClickPropertyMenuItem,
  onClose = () => {},
  onOpen = () => {},
  propertiesPage,
  useAbsolutePaths = false,
  userModel = {} as User,
}: Props) => {
  const location = useLocation();

  const [isPropertiesDropdownOpen, setIsPropertiesDropdownOpen] = useState<boolean>(false);

  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
    } else {
      // run the effect for subsequent renders
      if (isPropertiesDropdownOpen) {
        if (onOpen) {
          onOpen();
        }
      } else {
        if (onClose) {
          onClose();
        }
      }
    }
  }, [isPropertiesDropdownOpen]);

  const appendQueryParam = ({ key, value }: { key: string; value: string }) => {
    // location hook persists previous search value when url has different values
    /**
     * we manage location from different parts of the application
     * location via react-use and react-router-dom
     * the location search in the url gets out of sync
     *
     * need to rely on window.location here when possible
     */
    const queryParams = new URLSearchParams(typeof window !== 'undefined' ? window?.location?.search : location.search);
    queryParams.set(key, value);

    return queryParams.toString();
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    if (onClickPropertyMenuItem) {
      onClickPropertyMenuItem(event);
    }
  };

  const emitCSEventOnAddPropertyClick = () => {
    HeaderNavigationClickstreamEvents.ClickAddPropertyFromDropdownMenu({ user: userModel });
  };

  const emitCSEventOnAddFeedClick = () => {
    HeaderNavigationClickstreamEvents.ClickAddFeedFromDropdownMenu({ user: userModel });
  };

  const navigateToPath = (queryKey: string) => {
    const targetPath = `${propertiesPage}?${appendQueryParam({ key: queryKey, value: 'true' })}`;

    if (
      typeof window !== 'undefined' &&
      typeof window.browserHistory !== 'undefined' &&
      escapeAllLinks === false &&
      useAbsolutePaths === false
    ) {
      window.browserHistory.push(targetPath);
    } else {
      window?.open(targetPath, '_self');
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent, queryKey: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();

      setIsPropertiesDropdownOpen(false);
      navigateToPath(queryKey);
    }
  };

  return (
    <div data-testid="PlusIconPropertyMenu">
      {canAccessAddFeedListingsModal ? (
        <MenuPopper
          triggered={
            <StyledNavigationDropdownMenu data-testid="VerticalNavigation-PropertiesDropDownMenu">
              <MenuItem
                onClick={(event: React.SyntheticEvent) => {
                  handleClickPropertyMenuItem(event);
                  emitCSEventOnAddPropertyClick();
                }}
                onKeyDown={(event: React.KeyboardEvent) => handleMenuKeyDown(event, 'postingPath')}
                data-eventid={HeaderNavigationClickstreamEvents.ClickAddPropertyFromDropdownMenu.data_event_id}
              >
                <AdaptiveAnchor
                  title={'Add a property'}
                  path={`${propertiesPage}?${appendQueryParam({ key: 'postingPath', value: 'true' })}`}
                  link={Link}
                  escape={escapeAllLinks}
                  useAbsolutePaths={useAbsolutePaths}
                >
                  <Flex display="flex" flexDirection="row" alignItems="center">
                    <IconHouseOutline size="xs" />
                    <Spacer marginRight="sm" />
                    <Text>Add a property</Text>
                  </Flex>
                </AdaptiveAnchor>
              </MenuItem>

              <MenuItem
                onClick={(event: React.SyntheticEvent) => {
                  handleClickPropertyMenuItem(event);
                  emitCSEventOnAddFeedClick();
                }}
                onKeyDown={(event: React.KeyboardEvent) => handleMenuKeyDown(event, 'addAFeed')}
                data-eventid={HeaderNavigationClickstreamEvents.ClickAddFeedFromDropdownMenu.data_event_id}
              >
                <AdaptiveAnchor
                  title={'Add a feed'}
                  path={`${propertiesPage}?${appendQueryParam({ key: 'addAFeed', value: 'true' })}`}
                  link={Link}
                  escape={escapeAllLinks}
                  useAbsolutePaths={useAbsolutePaths}
                >
                  <Flex display="flex" flexDirection="row" alignItems="center">
                    <IconLiveNow size="xs" />
                    <Spacer marginRight="sm" />
                    <Text>Add a feed</Text>
                  </Flex>
                </AdaptiveAnchor>
              </MenuItem>
            </StyledNavigationDropdownMenu>
          }
          isOpen={isPropertiesDropdownOpen}
          onOpen={() => {
            setIsPropertiesDropdownOpen(true);
            HeaderNavigationClickstreamEvents.OnPropertiesDropdownOpen({ user: userModel });
          }}
          onClose={() => {
            setIsPropertiesDropdownOpen(false);
          }}
          offset={[-120, 20]}
          placement="bottom"
        >
          <StyledIconButton
            title="Add a feed"
            size="sm"
            bare={true}
            buttonType="secondary"
            icon={<IconPlus fontColor="blue400" />}
            onClick={(event: React.SyntheticEvent) => {
              // prevent navigation
              event.preventDefault();
              event.stopPropagation();

              setIsPropertiesDropdownOpen(!isPropertiesDropdownOpen);
            }}
            data-eventid={HeaderNavigationClickstreamEvents.OnPropertiesDropdownOpen.data_event_id}
          />
        </MenuPopper>
      ) : (
        <StyledTooltipPopper
          triggered={<Tooltip body={<Text>Add a property</Text>} />}
          openWith={['mouseEnter']}
          mouseEnterDelay={300}
        >
          <div>
            <StyledIconButton
              title="Add a property"
              size="sm"
              bare={true}
              buttonType="secondary"
              icon={<IconPlus fontColor="blue400" />}
              onClick={(event: React.SyntheticEvent) => {
                // prevent navigation
                event.preventDefault();
                event.stopPropagation();

                handleNoFeedIconPlusClick(HeaderTitles.myProperties);
                navigateToPath('postingPath');
              }}
            />
          </div>
        </StyledTooltipPopper>
      )}
    </div>
  );
};

export default memo(PlusIconPropertyMenu);
